import { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import {
  getAlbums,
  getTracksSingles,
  deleteAlbum,
  deleteTrack,
  getCoverPublicUrl,
  type Album,
  type Track,
} from '../../services/api/albums';

export default function MusicManagerPage() {
  const { t } = useTranslation();
  const [albums, setAlbums] = useState<Album[]>([]);
  const [standaloneTracks, setStandaloneTracks] = useState<Track[]>([]);
  const [loading, setLoading] = useState(true);

  function load() {
    Promise.all([getAlbums(), getTracksSingles()])
      .then(([a, s]) => {
        setAlbums(a);
        setStandaloneTracks(s);
      })
      .catch(console.error)
      .finally(() => setLoading(false));
  }

  useEffect(() => {
    load();
  }, []);

  const albumsOnly = albums.filter((a) => !a.is_single);
  const singlesOnly = albums.filter((a) => a.is_single);

  async function handleDeleteAlbum(id: string) {
    if (!confirm(t('admin.deleteAlbumConfirm'))) return;
    try {
      await deleteAlbum(id);
      setAlbums((a) => a.filter((x) => x.id !== id));
    } catch (e) {
      console.error(e);
    }
  }

  async function handleDeleteTrack(id: string) {
    if (!confirm(t('admin.deleteTrackConfirm'))) return;
    try {
      await deleteTrack(id);
      setStandaloneTracks((s) => s.filter((x) => x.id !== id));
    } catch (e) {
      console.error(e);
    }
  }

  if (loading) return <p className="text-[var(--color-text-muted)]">{t('common.loading')}</p>;

  return (
    <div className="admin-page">
      <h1 className="admin-page__title">{t('admin.music')}</h1>

      {/* Section 1: Edit albums */}
      <section className="admin-music-section">
        <h2 className="admin-music-section__title">{t('admin.editAlbums')}</h2>
        <p className="admin-music-section__subtitle">{t('admin.yourAlbums')}</p>
        {albumsOnly.length === 0 ? (
          <p className="admin-page__empty">{t('admin.noAlbumsYet')}</p>
        ) : (
          <div className="admin-music-card-grid">
            {albumsOnly.map((a) => (
              <div key={a.id} className="admin-music-card">
                <div className="admin-music-card__cover">
                  {a.cover_storage_path ? (
                    <img
                      src={getCoverPublicUrl(a.cover_storage_path)}
                      alt=""
                      className="admin-music-card__img"
                    />
                  ) : (
                    <div className="admin-music-card__placeholder">{t('admin.noCover')}</div>
                  )}
                </div>
                <div className="admin-music-card__name">{a.title_en}</div>
                <div className="admin-music-card__actions">
                  <Link to={`/admin/music/albums/${a.id}`} className="admin-btn admin-btn--sm">
                    {t('common.edit')}
                  </Link>
                  <button
                    type="button"
                    onClick={() => handleDeleteAlbum(a.id)}
                    className="admin-btn admin-btn--sm admin-btn--danger"
                  >
                    {t('common.delete')}
                  </button>
                </div>
              </div>
            ))}
          </div>
        )}
      </section>

      {/* Section 2: Single tracks (single releases + standalone tracks) */}
      <section className="admin-music-section">
        <h2 className="admin-music-section__title">{t('admin.singleTracks')}</h2>
        <p className="admin-music-section__subtitle">{t('admin.singleReleases')}</p>

        {singlesOnly.length > 0 && (
          <>
            <h3 className="admin-music-section__sub">{t('admin.singleReleasesAlbums')}</h3>
            <div className="admin-music-card-grid">
              {singlesOnly.map((a) => (
                <div key={a.id} className="admin-music-card">
                  <div className="admin-music-card__cover">
                    {a.cover_storage_path ? (
                      <img
                        src={getCoverPublicUrl(a.cover_storage_path)}
                        alt=""
                        className="admin-music-card__img"
                      />
                    ) : (
                      <div className="admin-music-card__placeholder">{t('admin.noCover')}</div>
                    )}
                  </div>
                  <div className="admin-music-card__name">{a.title_en}</div>
                  <div className="admin-music-card__actions">
                    <Link to={`/admin/music/albums/${a.id}`} className="admin-btn admin-btn--sm">
                      {t('common.edit')}
                    </Link>
                    <button
                      type="button"
                      onClick={() => handleDeleteAlbum(a.id)}
                      className="admin-btn admin-btn--sm admin-btn--danger"
                    >
                      {t('common.delete')}
                    </button>
                  </div>
                </div>
              ))}
            </div>
          </>
        )}

        {standaloneTracks.length > 0 && (
          <>
            <h3 className="admin-music-section__sub">{t('admin.standaloneTracks')}</h3>
            <p className="admin-music-section__subtitle">{t('admin.standaloneTracksHint')}</p>
            <ul className="admin-music-standalone-list">
              {standaloneTracks.map((tr) => (
                <li key={tr.id} className="admin-music-standalone-item">
                  <span className="admin-music-standalone-item__title">{tr.title_en}</span>
                  <div className="admin-music-standalone-item__actions">
                    <Link to={`/admin/music/tracks/${tr.id}`} className="admin-btn admin-btn--sm">
                      {t('common.edit')}
                    </Link>
                    <button
                      type="button"
                      onClick={() => handleDeleteTrack(tr.id)}
                      className="admin-btn admin-btn--sm admin-btn--danger"
                    >
                      {t('common.delete')}
                    </button>
                  </div>
                </li>
              ))}
            </ul>
          </>
        )}

        {singlesOnly.length === 0 && standaloneTracks.length === 0 && (
          <p className="admin-page__empty">{t('admin.noSinglesYet')}</p>
        )}
      </section>

      {/* Section 3: Create album / Add single track */}
      <section className="admin-music-section admin-music-create">
        <h2 className="admin-music-section__title">{t('admin.createSection')}</h2>
        <div className="admin-music-create-grid">
          <div className="admin-music-create-card">
            <h3 className="admin-music-create-card__title">{t('admin.createAlbum')}</h3>
            <p className="admin-music-create-card__hint">{t('admin.createNewAlbumHint')}</p>
            <Link
              to="/admin/music/new"
              state={{ type: 'album' }}
              className="admin-btn admin-btn--primary"
            >
              {t('admin.createAlbum')}
            </Link>
          </div>
          <div className="admin-music-create-card">
            <h3 className="admin-music-create-card__title">{t('admin.addSingleTrack')}</h3>
            <p className="admin-music-create-card__hint">{t('admin.addSingleTrackHint')}</p>
            <Link
              to="/admin/music/new"
              state={{ type: 'single' }}
              className="admin-btn admin-btn--primary"
            >
              {t('admin.addSingleTrack')}
            </Link>
          </div>
        </div>
      </section>
    </div>
  );
}
